import { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Flex,
	HStack,
	Input,
	NumberInput,
	NumberInputField,
	SimpleGrid,
	Spinner,
	Switch,
	Text,
	Textarea,
	Tooltip,
} from '@chakra-ui/react';

import useAppToast from 'components/toast';
import { Banner, SectionCard } from 'views/admin/finance-core/_ui/components';
import { TimeField } from 'views/admin/finance-core/_ui/TimeField';
import { useFinanceUI } from 'views/admin/finance-core/_ui/ui';
import { useGetCampaignSettingsQuery, useUpdateCampaignSettingsMutation } from 'api/metaWhatsappApi';
import campaignError from '../campaignErrors';
import TimezoneRules from './TimezoneRules';
import { cityMapFromRows, cityRowsFromMap, tzMapFromRows, tzRowsFromMap } from './timezoneRows';

/**
 * The tenant's campaign defaults.
 *
 * ★ EVERY META-FACING NUMBER IS DATA, NOT A CONSTANT. WhatsApp has changed its
 * tier ladder, its billing rules and its quality vocabulary more than once, and
 * an operator who needs a different number should change it here rather than
 * wait for a deploy. That is why this screen exists at all.
 *
 * ★ THE SERVER VALIDATES BY FIELD NAME AND REFUSES BY FIELD NAME. So this
 * screen does not re-implement the rules — it sends what was typed and shows
 * the server's sentence, which is the only one guaranteed to match what was
 * actually enforced.
 */

/** A labelled control with the reason it exists underneath. */
function Setting({ label, help, children }) {
	const ui = useFinanceUI();
	return (
		<Box mb={5}>
			<Flex justify='space-between' align='center' gap={4} mb={help ? 1 : 0}>
				<Text fontSize='sm' color={ui.textPrimary} fontWeight='medium'>
					{label}
				</Text>
				{children}
			</Flex>
			{help && (
				<Text fontSize='xs' color={ui.textMuted} maxW='46ch'>
					{help}
				</Text>
			)}
		</Box>
	);
}

export default function CampaignSettings() {
	const ui = useFinanceUI();
	const notify = useAppToast();

	const { data, isLoading, isError, refetch } = useGetCampaignSettingsQuery();
	const [update, { isLoading: isSaving }] = useUpdateCampaignSettingsMutation();

	// ⚠ THE RESPONSE IS AN ENVELOPE, not the settings document. Reading
	// `data.defaults` instead of `data.settings.defaults` is not a crash — every
	// field silently reads as undefined and falls back to a shipped default. It
	// looked like a working screen while showing none of the saved values, and
	// pressing Save sent an EMPTY opt-out keyword list, which the server refuses.
	const settings = data?.settings ?? null;
	const cityCatalogue = data?.cityCatalogue ?? {};

	const [form, setForm] = useState(null);

	useEffect(() => {
		if (!settings) return;
		setForm({
			capPercent: settings.defaults?.capPercent ?? 70,
			windowStart: settings.defaults?.window?.start ?? '09:00',
			windowEnd: settings.defaults?.window?.end ?? '20:00',
			frequencyEnabled: settings.defaults?.frequencyCap?.enabled ?? true,
			frequencyCount: settings.defaults?.frequencyCap?.count ?? 1,
			frequencyDays: settings.defaults?.frequencyCap?.days ?? 7,
			attributionDays: settings.defaults?.attributionDays ?? 7,
			failRatePct: settings.defaults?.thresholds?.failRatePct ?? 5,
			optOutRatePct: settings.defaults?.thresholds?.optOutRatePct ?? 5,
			defaultRegion: settings.defaults?.defaultRegion ?? 'AE',
			defaultTimezone: settings.defaults?.defaultTimezone ?? 'Asia/Dubai',
			// Empty means "always send a template", which is the shipped default.
			freeFormInWindowFor: settings.defaults?.freeFormInWindowFor ?? [],
			optOutKeywords: (settings.optOutKeywords ?? []).join(', '),
			optOutConfirmation: settings.optOutConfirmation ?? '',
			// Both maps are edited as ROWS: an object keyed by country cannot be
			// typed into, because renaming a key mid-edit loses the row.
			tzRows: tzRowsFromMap(settings.tzDefaults),
			cityRows: cityRowsFromMap(settings.cityZones),
		});
	}, [settings]);

	const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

	const onSave = async () => {
		try {
			await update({
				defaults: {
					capPercent: Number(form.capPercent),
					window: { start: form.windowStart, end: form.windowEnd },
					frequencyCap: {
						enabled: form.frequencyEnabled,
						count: Number(form.frequencyCount),
						days: Number(form.frequencyDays),
					},
					attributionDays: Number(form.attributionDays),
					thresholds: {
						failRatePct: Number(form.failRatePct),
						optOutRatePct: Number(form.optOutRatePct),
					},
					defaultRegion: form.defaultRegion,
					defaultTimezone: form.defaultTimezone,
					freeFormInWindowFor: form.freeFormInWindowFor ?? [],
				},
				optOutKeywords: form.optOutKeywords
					.split(',')
					.map((k) => k.trim())
					.filter(Boolean),
				optOutConfirmation: form.optOutConfirmation,
				// Map-valued settings are replaced whole rather than merged, so the
				// complete map goes every time — a removed row has to be able to
				// disappear.
				tzDefaults: tzMapFromRows(form.tzRows),
				cityZones: cityMapFromRows(form.cityRows),
			}).unwrap();
			notify.success('Settings saved');
		} catch (err) {
			// The server names the field it refused; that sentence is better than
			// anything this screen could invent.
			notify.error('Could not save', campaignError(err));
		}
	};

	if (isLoading || !form) {
		return (
			<Flex justify='center' py={12}>
				<Spinner color={ui.accent} />
			</Flex>
		);
	}

	if (isError) {
		return (
			<Banner status='error' title='We could not load your settings' description='Try again in a moment.' />
		);
	}

	const numberField = (key, props = {}) => (
		<NumberInput size='sm' maxW='110px' value={form[key]} onChange={set(key)} {...props}>
			<NumberInputField bg={ui.inputBg} borderColor={ui.border} color={ui.textPrimary} />
		</NumberInput>
	);

	return (
		<Box>
			{settings?.emergency?.active && (
				<Box mb={5}>
					<Banner
						status='error'
						title='Emergency stop is active'
						description='No campaign in this workspace is sending. Lift it from a campaign screen when you are ready.'
					/>
				</Box>
			)}

			<SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
				<SectionCard title='Pace' subtitle='How hard a number may be worked.'>
					<Setting
						label='Daily cap'
						help='A share of whatever tier WhatsApp currently allows each number. WhatsApp raises a tier only when a number uses at least half its limit for a week, so a flat ceiling would freeze it there permanently.'
					>
						<HStack spacing={2}>
							{numberField('capPercent', { min: 1, max: 100 })}
							<Text fontSize='sm' color={ui.textMuted}>
								%
							</Text>
						</HStack>
					</Setting>

					<Setting
						label='Sending window'
						help="The hours a campaign may send, in each recipient's OWN time zone — ten in the morning means ten where they are."
					>
						<HStack spacing={2}>
							<TimeField ariaLabel='Window start' maxW='120px' value={form.windowStart} onChange={set('windowStart')} />
							<Text fontSize='sm' color={ui.textMuted}>
								to
							</Text>
							<TimeField ariaLabel='Window end' maxW='120px' value={form.windowEnd} onChange={set('windowEnd')} />
						</HStack>
					</Setting>

					<Setting
						label='Default country'
						help='Used to read a phone number that carries no country code, and to place a contact whose country is unknown.'
					>
						<Input
							size='sm'
							maxW='90px'
							value={form.defaultRegion}
							onChange={(e) => set('defaultRegion')(e.target.value.toUpperCase())}
							bg={ui.inputBg}
							borderColor={ui.border}
							color={ui.textPrimary}
						/>
					</Setting>

					<Setting label='Default time zone' help='Where a contact is assumed to be when nothing better is known.'>
						<Input
							size='sm'
							maxW='190px'
							value={form.defaultTimezone}
							onChange={(e) => set('defaultTimezone')(e.target.value)}
							bg={ui.inputBg}
							borderColor={ui.border}
							color={ui.textPrimary}
						/>
					</Setting>
				</SectionCard>

				<SectionCard
				title='Cost'
				subtitle='Answering inside a conversation costs less than starting a new one.'
			>
				{/* ★ OFF UNLESS SOMEBODY CHOOSES IT, AND THE SCREEN SAYS WHY.
				    A customer who wrote to us in the last day is already in an open
				    conversation, so a plain message there is a service message
				    rather than a paid template. The saving is real — but whether
				    promotional content may travel that way is a judgement about
				    this business, and it lands on the WhatsApp number's quality
				    rating. So the control explains the trade-off instead of
				    presenting it as a free win. */}
				<Setting
					label='Send as a plain message when the customer wrote recently'
					help='Only for conversations the customer opened in the last day, and only when the message has no image, header or buttons. Anything else still sends as a template.'
				>
					<HStack spacing={4}>
						{[
							{ value: 'UTILITY', label: 'Utility' },
							{ value: 'MARKETING', label: 'Marketing' },
						].map((option) => (
							<Checkbox
								key={option.value}
								size='sm'
								colorScheme='yellow'
								isChecked={(form.freeFormInWindowFor ?? []).includes(option.value)}
								onChange={(e) => {
									const current = new Set(form.freeFormInWindowFor ?? []);
									if (e.target.checked) current.add(option.value);
									else current.delete(option.value);
									set('freeFormInWindowFor')([...current]);
								}}
							>
								<Text fontSize='sm' color={ui.textPrimary}>
									{option.label}
								</Text>
							</Checkbox>
						))}
					</HStack>
				</Setting>

				{(form.freeFormInWindowFor ?? []).includes('MARKETING') && (
					<Box mt={3}>
						<Banner
							status='warning'
							title='Check this against your own policy'
							description='Delivering promotional content as a plain message inside the service window is cheaper, but WhatsApp treats marketing and service messages differently. If they disagree with the call, it shows up in this number’s quality rating rather than as a refused send.'
						/>
					</Box>
				)}
			</SectionCard>

			<SectionCard title='Protecting your audience' subtitle='The rules that stop a campaign doing harm.'>
					<Setting
						label='Frequency cap'
						help='How often one person may receive a marketing campaign. Utility messages — reminders, documents somebody asked for — never count against it.'
					>
						<Switch
							size='sm'
							colorScheme='yellow'
							isChecked={form.frequencyEnabled}
							onChange={(e) => set('frequencyEnabled')(e.target.checked)}
						/>
					</Setting>

					{form.frequencyEnabled && (
						<Setting label='At most'>
							<HStack spacing={2}>
								{numberField('frequencyCount', { min: 1, max: 100 })}
								<Text fontSize='sm' color={ui.textMuted}>
									every
								</Text>
								{numberField('frequencyDays', { min: 1, max: 365 })}
								<Text fontSize='sm' color={ui.textMuted}>
									days
								</Text>
							</HStack>
						</Setting>
					)}

					<Setting
						label='Pause if failures reach'
						help='A campaign that is failing this often is doing damage to the number that sends it, so it stops itself.'
					>
						<HStack spacing={2}>
							{numberField('failRatePct', { min: 1, max: 100 })}
							<Text fontSize='sm' color={ui.textMuted}>
								%
							</Text>
						</HStack>
					</Setting>

					<Setting label='Pause if opt-outs reach' help='The clearest signal that a message is not welcome.'>
						<HStack spacing={2}>
							{numberField('optOutRatePct', { min: 1, max: 100 })}
							<Text fontSize='sm' color={ui.textMuted}>
								%
							</Text>
						</HStack>
					</Setting>

					<Setting
						label='Credit a reply for'
						help='How long after a campaign message a reply still counts as an answer to it.'
					>
						<HStack spacing={2}>
							{numberField('attributionDays', { min: 1, max: 90 })}
							<Text fontSize='sm' color={ui.textMuted}>
								days
							</Text>
						</HStack>
					</Setting>
				</SectionCard>
			</SimpleGrid>

			<SectionCard
				title='Time zones'
				subtitle='A campaign sends at each recipient’s own local hour — this is how we work out what that hour is.'
				mt={5}
			>
				<TimezoneRules
					tzRows={form.tzRows}
					setTzRows={set('tzRows')}
					cityRows={form.cityRows}
					setCityRows={set('cityRows')}
					catalogue={cityCatalogue}
					ui={ui}
				/>
			</SectionCard>

			<SectionCard
				title='Opting out'
				subtitle='What a customer can write to stop hearing from you, and what they are told.'
				mt={5}
			>
				<Text fontSize='sm' color={ui.textPrimary} fontWeight='medium' mb={1}>
					Words that mean stop
				</Text>
				<Text fontSize='xs' color={ui.textMuted} mb={2} maxW='70ch'>
					Matched exactly, after ignoring case, punctuation and Arabic vowel marks — so “STOP.” and “توقّف”
					both count, while “stop by tomorrow at 5” reaches an agent as an ordinary message. Separate with
					commas.
				</Text>
				<Textarea
					size='sm'
					rows={3}
					value={form.optOutKeywords}
					onChange={(e) => set('optOutKeywords')(e.target.value)}
					bg={ui.inputBg}
					borderColor={ui.border}
					color={ui.textPrimary}
					mb={5}
				/>

				<Text fontSize='sm' color={ui.textPrimary} fontWeight='medium' mb={1}>
					What they are told
				</Text>
				<Text fontSize='xs' color={ui.textMuted} mb={2} maxW='70ch'>
					Sent straight after they are removed. Leave it empty to say nothing at all — the removal still
					happens either way.
				</Text>
				<Textarea
					size='sm'
					rows={3}
					value={form.optOutConfirmation}
					onChange={(e) => set('optOutConfirmation')(e.target.value)}
					bg={ui.inputBg}
					borderColor={ui.border}
					color={ui.textPrimary}
				/>
			</SectionCard>

			<Flex justify='flex-end' mt={5}>
				<Button size='sm' bg={ui.accent} color={ui.textOnAccent} isLoading={isSaving} onClick={onSave}>
					Save settings
				</Button>
			</Flex>
		</Box>
	);
}
